import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { HeaderPro } from '../../components/HeaderPro';
import { Footer } from '../../components/Footer';
import { PrimaryButton } from '../../components/PrimaryButton';
import { useAuth } from '../../../lib/use-auth';
import { toast } from '../../../lib/toast';

/**
 * Connected-apps preferences. There's no backend OAuth integration
 * yet, so the "connect / disconnect" toggle just records the pro's
 * intent in localStorage. When real OAuth callbacks land, swap this
 * for getMe/updateMe on `connected_apps`.
 */
// Twitter is intentionally omitted from the UI for launch — kept the
// constant list short and clear instead of adding a `visible` flag.
// Add it back here when an OAuth integration is built for it.
const APP_DEFINITIONS = [
  { id: 'facebook', name: 'Facebook' },
  { id: 'google', name: 'Google' },
  { id: 'instagram', name: 'Instagram' },
  { id: 'dropbox', name: 'Dropbox' },
];

const storageKey = (userId: string | null | undefined) =>
  `cherrypicked.pro.connected_apps.${userId ?? 'anon'}`;

function loadState(userId: string | null | undefined): Record<string, boolean> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    if (!raw) return {};
    return JSON.parse(raw) as Record<string, boolean>;
  } catch {
    return {};
  }
}

function saveState(
  userId: string | null | undefined,
  state: Record<string, boolean>,
): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey(userId), JSON.stringify(state));
  } catch {
    // ignore
  }
}

export default function ProPrivacyPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [connections, setConnections] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setConnections(loadState(user?.id));
  }, [user?.id]);

  const toggleConnection = (id: string) => {
    setConnections((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSave = () => {
    setSaving(true);
    saveState(user?.id, connections);
    setSaving(false);
    toast.success('Preferences saved.');
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <HeaderPro />

      <main className="flex-1 max-w-[1140px] mx-auto w-full px-4 py-6 md:py-6 md:py-12">
        <div className="flex items-start gap-4 mb-10">
          <Link
            to="/professional/account-settings"
            className="mt-1 w-[40px] h-[40px] bg-[#3a3335]/[0.04] rounded-[10px] flex items-center justify-center hover:bg-[#3a3335]/[0.08] transition-colors shrink-0"
          >
            <span className="material-icons text-[#908e8e] text-[18px]">
              chevron_left
            </span>
          </Link>
          <div>
            <h1 className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[18px] mb-1">
              Privacy
            </h1>
            <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] tracking-[-0.1px]">
              Connected apps and services
            </p>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row justify-between gap-12">
          <div className="flex-1 max-w-[553px]">
            <div className="space-y-0 divide-y divide-[#eee]">
              {APP_DEFINITIONS.map((app) => {
                const connected = !!connections[app.id];
                return (
                  <div
                    key={app.id}
                    className="py-6 flex items-center justify-between group"
                  >
                    <div>
                      <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[12px] tracking-[-0.1px] mb-1">
                        {app.name}
                      </p>
                      <p className="font-['DM_Sans',sans-serif] font-bold text-[14px] tracking-[-0.1px] text-[#3a3335]">
                        {connected ? 'Connected' : 'Not connected'}
                      </p>
                    </div>

                    <button
                      onClick={() => toggleConnection(app.id)}
                      className="flex items-center gap-2 text-[#908e8e] hover:text-[#8e2a2f] transition-colors font-['DM_Sans',sans-serif] font-bold text-[12px] tracking-[-0.1px]"
                    >
                      <span className="material-icons text-[18px]">
                        {connected ? 'link_off' : 'link'}
                      </span>
                      {connected ? 'Remove' : 'Connect'}
                    </button>
                  </div>
                );
              })}
            </div>

            <div className="mt-12 flex items-center gap-4">
              <PrimaryButton
                className="w-auto px-6 h-[50px] text-[14px] whitespace-nowrap"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Update settings'}
              </PrimaryButton>
              <button
                onClick={() => navigate('/professional/account-settings')}
                className="px-8 h-[50px] rounded-[10px] font-['DM_Sans',sans-serif] font-bold text-[#908e8e] text-[14px] bg-[#3a3335]/[0.04] hover:bg-[#3a3335]/[0.08] transition-all"
              >
                Cancel
              </button>
            </div>
          </div>

          <div className="w-full lg:w-[361px] shrink-0">
            <div className="bg-white rounded-[6px] shadow-[0px_3px_4px_0px_rgba(153,155,168,0.15)] p-8">
              <div className="w-[48px] h-[48px] rounded-[8px] bg-[#3a3335]/[0.04] flex items-center justify-center mb-6">
                <span className="material-icons text-[#908e8e] text-[22px]">
                  visibility
                </span>
              </div>
              <h3 className="font-['Inter',sans-serif] font-bold text-[#3a3335] text-[16px] leading-[24px] mb-3">
                Setup profiles
              </h3>
              <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] leading-[21px] tracking-[-0.1px]">
                Connect your social accounts so customers can recognise you
                across platforms. OAuth integration is coming — for now these
                toggles record your preference locally.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
